import React, { useState, useEffect, memo, useRef } from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import { calculateNetCaseWeight, calculateGrossCaseWeight, calculateVolumeCubicFeet, formatQuantity } from '../utils/commonUtils';
import api from '../api/axiosConfig';

const InventoryItemCard = memo(function InventoryItemCard({ inventoryItem }) {
    const [isWrapped, setIsWrapped] = useState(false);
    const rowRef = useRef(null);

    // Function to get the full image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        // If it's already a full URL, return as is
        if (imagePath.startsWith('http')) return imagePath;
        // Otherwise, prepend the API base URL
        return `${api.defaults.baseURL}${imagePath}`;
    };

    useEffect(() => {
        if (!rowRef.current) return;

        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                // Get the first and second columns
                const columns = entry.target.children;
                if (columns.length < 2) return;

                // Check if columns are wrapped by comparing their y positions
                const firstColRect = columns[0].getBoundingClientRect();
                const lastColRect = columns[columns.length - 1].getBoundingClientRect();
                
                setIsWrapped(firstColRect.top !== lastColRect.top);
            }
        });

        observer.observe(rowRef.current);
        return () => observer.disconnect();
    }, []);

    const netCaseWeight = calculateNetCaseWeight(
        inventoryItem.stock_item.item_size,
        inventoryItem.stock_item.item_size_units,
        inventoryItem.stock_item.case_pack
    );

    const grossCaseWeight = calculateGrossCaseWeight(
        inventoryItem.stock_item.item_size,
        inventoryItem.stock_item.item_size_units,
        inventoryItem.stock_item.case_pack,
        inventoryItem.stock_item.package_weight,
        inventoryItem.stock_item.package_weight_units
    );

    const getCountryFlag = (countryCode) => {
        if (!countryCode || countryCode.length !== 2) return '';
        const OFFSET = 127397;
        const first = countryCode.charCodeAt(0) + OFFSET;
        const second = countryCode.charCodeAt(1) + OFFSET;
        return String.fromCodePoint(first, second);
    };

    return (
        <Card className="shadow-sm" >
            <Card.Body>
                <Card.Title className="justify-content-between align-items-center">
                    <span>{inventoryItem.stock_item.description}</span>
                </Card.Title>
                <Card.Text as="div">
                    <Row ref={rowRef}>
                        <Col>
                            <div style={{ position: 'relative' }}>
                                {inventoryItem.stock_item.image ? (
                                    <img 
                                        src={getImageUrl(inventoryItem.stock_item.image)} 
                                        alt={inventoryItem.stock_item.description} 
                                        className="rounded" 
                                        style={{ width: '340px', height: '340px' }}
                                    />
                                ) : (
                                    <div style={{ 
                                        width: '350px', 
                                        height: '350px', 
                                        backgroundColor: 'transparent', 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center' 
                                    }}>
                                        <i className="bi bi-image" style={{ fontSize: '50px', color: 'darkgray' }}></i>
                                    </div>
                                )}
                                <div className="d-flex flex-wrap gap-1 p-2" 
                                    style={{ 
                                        position: 'absolute', 
                                        top: 0, 
                                        left: 0, 
                                        right: 0,
                                        background: 'transparent',
                                        borderTopLeftRadius: '0.375rem',
                                        borderTopRightRadius: '0.375rem'
                                    }}>
                                    {inventoryItem.stock_item.categories_names.map((category, index) => (
                                        <Badge
                                            key={`category-${index}-${category}`}
                                            bg="primary"
                                            className="rounded"
                                        >
                                            {category}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </Col>
                        {!isWrapped ? (
                            <Col xs="auto" className="p-0" style={{ width: '1px' }}>
                                <div className="border-primary m-0" 
                                    style={{ 
                                        height: '100%', 
                                        borderStyle: 'dotted',
                                        borderWidth: '0px 2px 0px 0px',
                                        borderColor: 'var(--bs-primary)'
                                    }}>
                                </div>
                            </Col>
                        ) : (
                            <Col xs={12} className="p-0 my-3">
                                <div className="border-primary m-0" 
                                    style={{ 
                                        width: '100%', 
                                        borderStyle: 'dotted',
                                        borderWidth: '2px 0px 0px 0px',
                                        borderColor: 'var(--bs-primary)'
                                    }}>
                                </div>
                            </Col>
                        )}
                        <Col>
                            <table className="table table-borderless table-striped" style={{ marginBottom: 0 }}>
                                <tbody style={{ lineHeight: '1', fontSize: '0.85rem' }}>
                                    <tr>
                                        <td className="py-1" >MANUFACTURER:</td>
                                        <td className="py-1"><strong>{inventoryItem.stock_item.manufacturer_name || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >SKU:</td>
                                        <td className="py-1"><strong>{inventoryItem.stock_item.sku || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >GTIN:</td>
                                        <td className="py-1"><strong>{inventoryItem.stock_item.gtin || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >UPC:</td>
                                        <td className="py-1"><strong>{inventoryItem.stock_item.upc || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >COUNTRY:</td>
                                        <td className="py-1">
                                            {inventoryItem.stock_item.country_of_origin ? (
                                                <strong>
                                                    <span>{getCountryFlag(inventoryItem.stock_item.country_of_origin)}{' '}</span>
                                                    <span>{inventoryItem.stock_item.country_of_origin}</span>
                                                </strong>
                                            ) : <strong>N/A</strong>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >ITEM SIZE:</td>
                                        <td className="py-1"><strong>{inventoryItem.stock_item.item_size ? `${inventoryItem.stock_item.item_size} ${inventoryItem.stock_item.item_size_units}` : 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >CASE PACK:</td>
                                        <td className="py-1"><strong>{inventoryItem.stock_item.case_pack}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >NET CASE WEIGHT:</td>
                                        <td className="py-1"><strong>{netCaseWeight} lbs</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >GROSS CASE WEIGHT:</td>
                                        <td className="py-1"><strong>{grossCaseWeight} lbs</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1">
                                            <i className="bi bi-rulers"></i>
                                        </td>
                                        <td className="py-1">
                                            <strong>
                                                {inventoryItem.stock_item.dimensions_l && inventoryItem.stock_item.dimensions_w && inventoryItem.stock_item.dimensions_h ? (
                                                    `${inventoryItem.stock_item.dimensions_l}" × ${inventoryItem.stock_item.dimensions_w}" × ${inventoryItem.stock_item.dimensions_h}"`
                                                ) : 'N/A'}
                                            </strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >PACKAGE WEIGHT:</td>
                                        <td className="py-1"><strong>{inventoryItem.stock_item.package_weight || 'N/A'} {inventoryItem.stock_item.package_weight_units || 'lbs'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >TI/HI:</td>
                                        <td className="py-1"><strong>{inventoryItem.stock_item.ti || 'N/A'} / {inventoryItem.stock_item.hi || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >VOLUME:</td>
                                        <td className="py-1"><strong>{calculateVolumeCubicFeet(inventoryItem.stock_item.dimensions_l, inventoryItem.stock_item.dimensions_w, inventoryItem.stock_item.dimensions_h) || 'N/A'} cubic ft</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} className="p-0 my-3">
                                <div className="border-primary m-0" 
                                    style={{ 
                                        width: '100%', 
                                        borderStyle: 'dotted',
                                        borderWidth: '2px 0px 0px 0px',
                                        borderColor: 'var(--bs-primary)'
                                    }}>
                                </div>
                            </Col>
                            <Col>
                            <table className="table table-borderless table-striped" style={{ marginBottom: 0 }}>
                                <tbody style={{ lineHeight: '1', fontSize: '0.85rem' }}>
                                    <tr>
                                        <td className="py-1" >LOT:</td>
                                        <td className="py-1"><strong>{inventoryItem.lot}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >NOTES:</td>
                                        <td className="py-1"><strong>{inventoryItem.notes}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >BB DATE:</td>
                                        <td className="py-1"><strong>{inventoryItem.bb_date}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1">WAREHOUSE:</td>
                                        <td className="py-1"><strong>{inventoryItem.warehouse_name || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1">LOCATION:</td>
                                        <td className="py-1"><strong>{inventoryItem.location|| 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1">QUANTITY:</td>
                                        <td className="py-1"><strong>{formatQuantity(inventoryItem.quantity) || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1">VENDOR:</td>
                                        <td className="py-1"><strong>{inventoryItem.vendor || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1">COST:</td>
                                        <td className="py-1"><strong>${inventoryItem.cost || 'N/A'}</strong></td>
                                    </tr>
                                </tbody>
                            </table> 
                        </Col>
                    </Row>
                </Card.Text>
            </Card.Body>
        </Card>
    );
});

export default InventoryItemCard;